import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { TrendingUp, LogOut, Search, Bell, ChevronDown, MessageSquare, Users, HelpCircle, Gift, Menu, X } from 'lucide-react';
import toast from 'react-hot-toast';

const Navbar = () => {
    const { user, logout } = useAuthStore();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState('');
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
    };

    return (
        <>
            <nav className="bg-[#181a20] border-b border-gray-800 sticky top-0 z-50">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-8">
                            <Link to="/" className="flex items-center gap-2 text-xl font-bold text-white">
                                <TrendingUp className="text-blue-500" />
                                <span>Albatech</span>
                            </Link>
                            <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-400">
                                <Link
                                    to="/dashboard"
                                    className={`hover:text-white transition ${location.pathname === '/dashboard' ? 'text-white' : ''}`}
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/markets"
                                    className={`hover:text-white transition ${location.pathname === '/markets' ? 'text-white' : ''}`}
                                >
                                    Markets
                                </Link>

                                {/* Trade Dropdown */}
                                <div className="relative group">
                                    <button
                                        className={`flex items-center gap-1 hover:text-white transition ${location.pathname.includes('/trade') ||
                                            location.pathname.includes('/futures') ||
                                            location.pathname.includes('/p2p')
                                            ? 'text-white'
                                            : ''
                                            }`}
                                    >
                                        Trade <ChevronDown size={14} />
                                    </button>
                                    <div className="absolute top-full left-0 mt-0 w-40 bg-[#1e2329] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden py-1">
                                        <Link to="/convert" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Convert</Link>
                                        <Link to="/trade/BTCUSDT" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Spot</Link>
                                        <Link to="/futures/BTCUSDT" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Future</Link>
                                        <Link to="/p2p" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">P2P</Link>
                                    </div>
                                </div>

                                <Link
                                    to="/wallet"
                                    className={`hover:text-white transition ${location.pathname === '/wallet' ? 'text-white' : ''}`}
                                >
                                    Wallet
                                </Link>

                                {/* Services Dropdown */}
                                <div className="relative group">
                                    <button
                                        className={`flex items-center gap-1 hover:text-white transition ${location.pathname.includes('/bot-trading') || location.pathname.includes('/futures-bot')
                                            ? 'text-white'
                                            : ''
                                            }`}
                                    >
                                        Services <ChevronDown size={14} />
                                    </button>
                                    <div className="absolute top-full left-0 mt-0 w-52 bg-[#1e2329] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden py-1">
                                        <Link to="/staking" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Staking</Link>
                                        <Link to="/launchpad" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Launchpad</Link>
                                        <Link to="/announcements" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">Announcements</Link>
                                        <div className="border-t border-gray-700 my-1"></div>
                                        <Link to="/bot-trading" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">🤖 Spot Bot Trading</Link>
                                        <Link to="/futures-bot" className="block px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-yellow-400 transition whitespace-nowrap">⚡ Futures Bot Trading</Link>
                                    </div>
                                </div>

                                {/* Referrals Link */}
                                <Link
                                    to="/referrals"
                                    className={`flex items-center gap-1 hover:text-white transition ${location.pathname === '/referrals' ? 'text-yellow-400' : ''}`}
                                >
                                    <Gift size={14} className="text-yellow-400" />
                                    Referral
                                </Link>

                                {/* Support Link */}
                                <Link
                                    to="/support"
                                    className={`flex items-center gap-1 hover:text-white transition ${location.pathname === '/support' ? 'text-white' : ''}`}
                                >
                                    <HelpCircle size={14} />
                                    Support
                                </Link>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4">
                            {/* Mobile Menu Button */}
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className="md:hidden p-2 text-gray-400 hover:text-white transition"
                            >
                                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>

                            <div className="hidden md:flex items-center gap-2 text-sm text-gray-400 bg-gray-800/50 px-3 py-1.5 rounded-full">
                                <Search size={14} />
                                <input
                                    type="text"
                                    placeholder="Search coin"
                                    className="bg-transparent border-none focus:outline-none w-24 text-white placeholder-gray-500"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button className="p-2 text-gray-400 hover:text-white transition relative">
                                <Bell size={20} />
                                <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"></span>
                            </button>

                            {/* User Dropdown */}
                            <div className="relative group">
                                <button className="flex items-center gap-2 focus:outline-none">
                                    <div className="h-8 w-8 bg-gradient-to-br from-[#fcd535] to-[#f0b90b] rounded-full flex items-center justify-center text-black font-bold cursor-pointer hover:opacity-90 transition">
                                        {user?.firstName?.[0]?.toUpperCase()}
                                    </div>
                                    <div className="hidden md:block text-left">
                                        <div className="text-sm font-medium text-white">{user?.firstName}</div>
                                    </div>
                                    <ChevronDown size={14} className="text-gray-400 hidden md:block" />
                                </button>

                                <div className="absolute right-0 top-full mt-2 w-48 bg-[#1e2329] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden py-1">
                                    <div className="px-4 py-3 border-b border-gray-700">
                                        <p className="text-sm text-white font-medium">{user?.firstName} {user?.lastName}</p>
                                        <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                                    </div>
                                    <Link to="/profile" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">
                                        <div className="w-5"><TrendingUp size={16} /></div>
                                        Profile & KYC
                                    </Link>
                                    <Link to="/referrals" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">
                                        <div className="w-5"><Users size={16} /></div>
                                        Referrals
                                    </Link>
                                    <Link to="/support" className="flex items-center gap-2 px-4 py-2 text-sm text-gray-300 hover:bg-[#2b3139] hover:text-white transition">
                                        <div className="w-5"><MessageSquare size={16} /></div>
                                        Support
                                    </Link>
                                    <button onClick={handleLogout} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-[#2b3139] hover:text-red-300 transition text-left">
                                        <div className="w-5"><LogOut size={16} /></div>
                                        Logout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Navigation Drawer */}
            {
                isMenuOpen && (
                    <>
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black/60 z-40 md:hidden"
                            onClick={() => setIsMenuOpen(false)}
                        />

                        {/* Drawer */}
                        <div className="fixed top-0 right-0 h-full w-72 bg-[#181a20] border-l border-gray-800 z-50 md:hidden transform transition-transform duration-300 ease-in-out overflow-y-auto">
                            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                                <span className="text-lg font-bold text-white">Menu</span>
                                <button
                                    onClick={() => setIsMenuOpen(false)}
                                    className="p-2 text-gray-400 hover:text-white transition"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="p-4 space-y-2">
                                <Link
                                    to="/dashboard"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/dashboard' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/markets"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/markets' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Markets
                                </Link>

                                <div className="pt-2 pb-1">
                                    <span className="px-4 text-xs text-gray-500 uppercase tracking-wider">Trade</span>
                                </div>
                                <Link
                                    to="/trade/BTCUSDT"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname.includes('/trade') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Spot Trading
                                </Link>
                                <Link
                                    to="/futures/BTCUSDT"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname.includes('/futures') ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Futures Trading
                                </Link>
                                <Link
                                    to="/p2p"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/p2p' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    P2P Trading
                                </Link>

                                <div className="pt-2 pb-1">
                                    <span className="px-4 text-xs text-gray-500 uppercase tracking-wider">Services</span>
                                </div>
                                <Link
                                    to="/bot-trading"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/bot-trading' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Spot Bot Trading
                                </Link>

                                <div className="pt-2 pb-1">
                                    <span className="px-4 text-xs text-gray-500 uppercase tracking-wider">Account</span>
                                </div>
                                <Link
                                    to="/wallet"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/wallet' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Wallet
                                </Link>
                                <Link
                                    to="/referrals"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/referrals' ? 'bg-yellow-500/20 text-yellow-400' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    <Gift size={16} className="text-yellow-400" /> Referral
                                </Link>
                                <Link
                                    to="/profile"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/profile' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Profile & KYC
                                </Link>
                                <Link
                                    to="/support"
                                    onClick={() => setIsMenuOpen(false)}
                                    className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${location.pathname === '/support' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'}`}
                                >
                                    Support
                                </Link>

                                <div className="pt-4">
                                    <button
                                        onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                                        className="w-full flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/10 transition"
                                    >
                                        <LogOut size={16} /> Logout
                                    </button>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }
        </>
    );
};

export default Navbar;

